import { action, computed, makeObservable, observable } from 'mobx'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { Cubit } from './core'
import { PromptType } from '../firestore/PromptVersion/types'
import {
  InteractiveElementPromptVersion,
  PostRoomStatePromptVersion,
  RubricPromptVersion,
} from '../models/PromptVersion'
import { RubricType, type StaticModelCollection } from '../types'
import { PostRoomStatePromptType } from '../firestore/PromptPostRoomStateType/types'
import { getPromptVersions } from '../firestore/PromptVersion'
import { InteractiveElementPromptType } from '../firestore/PromptInteractiveElementType/types'
import { CurrentPromptVersion } from '../models/CurrentPromptVersion'
import { getCurrentPromptVersions } from '../firestore/CurrentPromptVersion'
import { PromptRubricType } from '../models/PromptRubricType'
import { PromptPostRoomStateType } from '../models/PromptPostRoomStateType'
import { PromptInteractiveElementType } from '../models/PromptInteractiveElementType'
import {
  getPromptPostRoomStateType,
  updatePromptPostRoomStateType,
} from '../firestore/PromptPostRoomStateType'
import {
  getRoomPromptType,
  updatePromptRubricType,
  updatePromptRubricTypeScoreDescription,
} from '../firestore/PromptRubricType'
import {
  getPromptInteractiveElementType,
  updatePromptInteractiveElementType,
} from '../firestore/PromptInteractiveElementType'

export class AdminPromptCubit<Tab extends string> extends Cubit {
  repository: FirebaseRepository

  promptAndSubType:
    | { promptType: PromptType.rubric; subType: RubricType }
    | { promptType: PromptType.postRoomState; subType: PostRoomStatePromptType }
    | {
        promptType: PromptType.interactiveElement
        subType: InteractiveElementPromptType
      }

  prompt:
    | PromptRubricType
    | PromptPostRoomStateType
    | PromptInteractiveElementType

  versions: StaticModelCollection<
    | RubricPromptVersion
    | PostRoomStatePromptVersion
    | InteractiveElementPromptVersion
  >

  currentVersions: StaticModelCollection<CurrentPromptVersion>

  @observable tab: Tab

  constructor(
    repository: FirebaseRepository,
    {
      promptType,
      typeId,
      defaultTab,
    }: { promptType: PromptType; typeId: string; defaultTab: Tab }
  ) {
    super()
    makeObservable(this)
    this.repository = repository
    this.tab = defaultTab

    this.promptAndSubType = parsePromptAndSubtype(promptType, typeId)

    switch (promptType) {
      case PromptType.rubric: {
        this.prompt = PromptRubricType.empty(repository)
        this.versions = RubricPromptVersion.emptyCollection(repository)
        break
      }
      case PromptType.postRoomState: {
        this.prompt = PromptPostRoomStateType.empty(repository)
        this.versions = PostRoomStatePromptVersion.emptyCollection(repository)
        break
      }
      case PromptType.interactiveElement: {
        this.prompt = PromptInteractiveElementType.empty(repository)
        this.versions =
          InteractiveElementPromptVersion.emptyCollection(repository)
        break
      }
    }

    this.currentVersions = CurrentPromptVersion.emptyCollection(repository)
  }

  initialize(): void {
    switch (this.promptAndSubType.promptType) {
      case PromptType.rubric: {
        const { subType, promptType } = this.promptAndSubType
        this.addStream(
          getPromptVersions(this.repository, {
            promptType,
            subType,
          }),
          (m) => this.versions.replaceModels(m)
        )

        this.addStream(
          getCurrentPromptVersions(this.repository, {
            promptType,
            subType,
          }),
          (m) => this.currentVersions.replaceModels(m)
        )

        this.addStream(
          getRoomPromptType(this.repository, { id: subType.toString() }),
          (m) => {
            if (!(this.prompt instanceof PromptRubricType))
              throw new Error(`Prompt is not a PromptRubricType`)
            this.prompt.replaceModel(m)
          }
        )

        break
      }
      case PromptType.postRoomState: {
        const { subType, promptType } = this.promptAndSubType
        this.addStream(
          getPromptVersions(this.repository, {
            promptType,
            subType,
          }),
          (m) => this.versions.replaceModels(m)
        )

        this.addStream(
          getCurrentPromptVersions(this.repository, {
            promptType,
            subType,
          }),
          (m) => {
            this.currentVersions.replaceModels(m)
          }
        )

        this.addStream(
          getPromptPostRoomStateType(this.repository, {
            type: subType,
          }),
          (m) => {
            if (!(this.prompt instanceof PromptPostRoomStateType))
              throw new Error(`Prompt is not PromptPostRoomStateType`)
            this.prompt.replaceModel(m)
          }
        )

        break
      }
      case PromptType.interactiveElement: {
        const { subType, promptType } = this.promptAndSubType
        this.addStream(
          getPromptVersions(this.repository, {
            promptType,
            subType,
          }),
          (m) => this.versions.replaceModels(m)
        )

        this.addStream(
          getCurrentPromptVersions(this.repository, {
            promptType,
            subType,
          }),
          (m) => {
            this.currentVersions.replaceModels(m)
          }
        )

        this.addStream(
          getPromptInteractiveElementType(this.repository, {
            type: subType,
          }),
          (m) => {
            if (!(this.prompt instanceof PromptInteractiveElementType))
              throw new Error(`Prompt is not PromptInteractiveElementType`)
            this.prompt.replaceModel(m)
          }
        )

        break
      }
    }
  }

  updatePromptDetails = async (params: {
    title: string
    description: string
  }) => {
    const { promptType, subType } = this.promptAndSubType

    switch (promptType) {
      case PromptType.rubric:
        return await updatePromptRubricType(this.repository, {
          ...params,
          rubricType: subType,
        })
      case PromptType.postRoomState:
        return await updatePromptPostRoomStateType(this.repository, {
          ...params,
          postRoomStateType: subType,
        })
      case PromptType.interactiveElement:
        return await updatePromptInteractiveElementType(this.repository, {
          ...params,
          interactiveElementType: subType,
        })
    }
  }

  updateRubricPromptScoreDescription = async (
    args: Omit<
      Parameters<typeof updatePromptRubricTypeScoreDescription>[1],
      'rubricType'
    >
  ) => {
    const { promptType, subType } = this.promptAndSubType

    if (promptType !== PromptType.rubric)
      throw new Error('Prompt type is not rubric')

    return await updatePromptRubricTypeScoreDescription(this.repository, {
      ...args,
      rubricType: subType,
    })
  }

  @computed
  get currentVersion() {
    if (!this.currentVersions.isLoaded)
      return CurrentPromptVersion.empty(this.repository)

    if (!this.currentVersions.models.length)
      throw new Error('No current version found')

    return this.currentVersions.models[0]
  }

  @computed
  get ready(): boolean {
    return (
      this.prompt.isLoaded &&
      this.versions.isLoaded &&
      this.currentVersions.isLoaded
    )
  }

  @computed
  get promptType() {
    return this.promptAndSubType.promptType
  }

  @computed
  get subType() {
    return this.promptAndSubType.subType
  }

  @action
  changeTab(tab: typeof this.tab): void {
    this.tab = tab
  }
}

function parsePromptAndSubtype(
  promptType: PromptType,
  typeId: string
): AdminPromptCubit<'irrelevant'>['promptAndSubType'] {
  switch (promptType) {
    case PromptType.rubric: {
      if (!Object.values(RubricType).includes(Number(typeId) as RubricType))
        throw new Error(`Invalid RubricType: ${typeId}`)
      return { promptType, subType: Number(typeId) as RubricType }
    }
    case PromptType.postRoomState: {
      if (
        !Object.values(PostRoomStatePromptType).includes(
          typeId as PostRoomStatePromptType
        )
      )
        throw new Error(`Invalid PostRoomStatePromptType: ${typeId}`)
      return { promptType, subType: typeId as PostRoomStatePromptType }
    }
    case PromptType.interactiveElement: {
      if (
        !Object.values(InteractiveElementPromptType).includes(
          typeId as InteractiveElementPromptType
        )
      )
        throw new Error(`Invalid PostRoomStatePromptType: ${typeId}`)
      return { promptType, subType: typeId as InteractiveElementPromptType }
    }
  }
}
